import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { ArrowOutlink } from '@syconium/little-miss-figgy'

import { PreviewPanelLauncher } from '../../app/_components/chrome/PreviewPanelLauncher.client'
import {
  NextDialogProvider,
  NextDialogTrigger,
} from '../../app/_components/dialogs/NextDialog.client'
import { OrganizationSelectionDialog } from '../../app/_components/dialogs/OrganizationSelectionDialog.client'
import { useAuthentication } from '../../app/_providers/AuthenticationProvider.client'
import { useLocalization } from '../../app/_providers/LocalizationProvider.client'
import { useOrganization } from '../../app/_providers/OrganizationProvider.client'
import { usePreviewPanel } from '../../app/_providers/PreviewPanelProvider.client'
import { useTranslation } from '../../app/_providers/TranslationProvider.client'
import { NavListItem } from '../../types/graphql/site-fixtures'

import { ActionButton, PreviewPanelWrap } from './NavListItem/styles'

/**
 * Logged Out:
 * - Log In
 * - Create Account
 *
 * Logged In:
 * - Profile
 * - Favorites
 * - Orders
 * - Addresses
 * - (Organization block: TEAMS CTA + badge if single admin org, admin dashboard, team store)
 * - Log Out
 */
export const useAccountMenuItems = () => {
  const {
    magnolia: {
      account: strings,
      navigation: { organizations: orgStrings },
    },
  } = useTranslation()
  const { isLoggedIn, logOut: logOutFunction } = useAuthentication()
  const { isUSRegion } = useLocalization()
  const { organization, organizations, teamStore } = useOrganization()
  const router = useRouter()
  const { showPreviewPanel } = usePreviewPanel()

  const adminOrganizations = useMemo(
    () => (organizations ?? []).filter(o => ['OWNER', 'ADMIN'].includes(o.currentUserRole)),
    [organizations]
  )

  const accountMenuItems: NavListItem[] = useMemo(() => {
    const createAccount = item({
      href: '/account/register',
      text: strings.createAccount,
      trackingLabel: 'Register',
    })
    const logIn = item({
      href: '/account/login',
      text: strings.logIn,
      trackingLabel: 'Log in',
    })
    const previewPanel = item({
      hasDivider: true,
      text: 'Preview Panel',
      render: _ => (
        <PreviewPanelWrap>
          <PreviewPanelLauncher />
        </PreviewPanelWrap>
      ),
    })
    const logOut = item({
      hasDivider: true,
      onClickish: () => logOutFunction(),
      text: strings.logOut,
    })
    const profile = item({
      onClickish: () => router.push('/myfigs'),
      text: strings.navigation.profile,
    })
    const orders = item({
      onClickish: () => router.push('/myfigs/orders'),
      text: strings.navigation.orders,
    })
    const addresses = item({
      onClickish: () => router.push('/myfigs/addresses'),
      text: strings.navigation.addresses,
    })
    const favorites = item({
      onClickish: () => router.push('/favorites'),
      text: strings.navigation.favorites,
    })

    const baseLoggedInItems = [
      profile,
      favorites,
      orders,
      ...(isUSRegion ? [addresses] : []),
      ...(showPreviewPanel ? [previewPanel] : []),
      logOut,
    ]

    if (!isLoggedIn) {
      return [logIn, createAccount, favorites]
    }

    const hasOrgContext = (organizations && organizations.length > 0) || organization || teamStore

    if (!hasOrgContext) {
      return baseLoggedInItems
    }

    const teamsCtaRow = item({
      hasDivider: true,
      href: '/myfigs/teams',
      text: orgStrings.teamsCTA,
      isB2B: true,
      ...(adminOrganizations.length === 1 && adminOrganizations[0]
        ? {
            badge:
              adminOrganizations[0].currentUserRole === 'OWNER'
                ? orgStrings.teamOwner
                : orgStrings.teamAdmin,
            badgeStyle: 'default' as const,
            badgeColorVariant:
              adminOrganizations[0].currentUserRole === 'OWNER'
                ? ('primary' as const)
                : ('info' as const),
          }
        : {}),
    })

    const adminDashboardRow =
      adminOrganizations.length > 0
        ? adminOrganizations.length === 1
          ? item({
              href: `/organizations/${adminOrganizations[0]?.id}`,
              text: orgStrings.adminDashboard,
              trailingIcon: <ArrowOutlink width={20} height={20} />,
              isB2B: true,
              trackingLabel: 'Admin Dashboard',
              id: `org-dashboard-${adminOrganizations[0]?.id}`,
            })
          : item({
              text: orgStrings.adminDashboard,
              trailingIcon: <ArrowOutlink width={20} height={20} />,
              isB2B: true,
              id: 'org-dashboard-selection',
              render: content => (
                <NextDialogProvider>
                  <NextDialogTrigger
                    render={<ActionButton type='button'>{content}</ActionButton>}
                  />
                  <OrganizationSelectionDialog />
                </NextDialogProvider>
              ),
            })
        : null

    const teamStoreRow = teamStore
      ? item({
          href: `/team-stores/${teamStore.id}`,
          text: teamStore.name,
          isB2B: true,
          id: `team-store-${teamStore.id}`,
        })
      : null

    const orgBlock = [
      teamsCtaRow,
      ...(adminDashboardRow ? [adminDashboardRow] : []),
      ...(teamStoreRow ? [teamStoreRow] : []),
    ]

    return [
      profile,
      favorites,
      orders,
      ...(isUSRegion ? [addresses] : []),
      ...orgBlock,
      ...(showPreviewPanel ? [previewPanel] : []),
      logOut,
    ]
  }, [
    isLoggedIn,
    isUSRegion,
    logOutFunction,
    organization,
    organizations,
    teamStore,
    adminOrganizations,
    orgStrings.adminDashboard,
    orgStrings.teamOwner,
    orgStrings.teamAdmin,
    orgStrings.teamsCTA,
    router,
    showPreviewPanel,
    strings.createAccount,
    strings.logIn,
    strings.logOut,
    strings.navigation.addresses,
    strings.navigation.orders,
    strings.navigation.profile,
    strings.navigation.favorites,
  ])

  return {
    accountMenuItems,
    adminOrganizations,
  }
}

function item(input: Partial<NavListItem> & { text: string }): NavListItem {
  return {
    __typename: 'NavListItem',
    badge: null,
    badgeColor: null,
    badgeTextColor: null,
    href: null,
    icon: null,
    isBold: null,
    isHeading: null,
    textColor: null,
    htmlId: null,
    htmlClass: null,
    isBtn: null,
    alternateModelVariant: null,
    id: input.text || '',
    ...input,
  }
}
